import { useEffect } from "react";

/**
 * The handover from the splash in `public/index.html` to the app.
 *
 * The splash is markup, not a component, because it has to be on screen before
 * there is a bundle to render one — see the comment beside it in `index.html`.
 * That leaves this side two things to decide: when the app is ready to be
 * looked at, and how the cover comes off.
 *
 * ## It stays up until there is a screen, not until there is a React
 *
 * A cold open on a phone used to run splash → spinner → "Loading your
 * workspace" → the dashboard: three waiting screens in a row, each one
 * replacing the last, none of them the app. The reader cannot tell progress
 * from churn, and the churn is all they see.
 *
 * So the splash does not come down when React paints. It comes down when the
 * app has something worth uncovering, and everything that is itself a wait
 * holds it up in the meantime — `SessionLoading` while Clerk restores the
 * session, `WorkspaceLoading` while whoami and the workspace call are in
 * flight. One screen, from the home icon to the dashboard, with the work
 * happening behind it.
 *
 * Holds are counted rather than flagged, for the same reason the tab bar's
 * are: one loader handing to the next must not let the cover off between
 * them, and React runs an effect twice on mount in development. And a release
 * is never acted on in the same task it arrives in — a swap unmounts the old
 * loader before it mounts the new one, so the count touches zero in the middle
 * of a commit that was never going to show anything.
 *
 * ## The floor, and the ceiling
 *
 * A floor, because a warm reload has the bundle in cache and the whole thing
 * would be up for forty milliseconds — long enough to be seen, too short to be
 * read, which lands as a glitch rather than as a screen. So it keeps its place
 * for `MINIMUM_MS` from the moment it went up.
 *
 * A ceiling, in `index.html` rather than here: ten seconds, after which the
 * splash is removed whatever anyone is holding. A handshake that slow has gone
 * wrong, and the loading screens underneath — the step labels, the retry on a
 * failed workspace — are the ones that can say so. The splash cannot.
 */

/** How long the splash stays up, counted from when `index.html` showed it. */
const MINIMUM_MS = 650;

/** Matches the `transition` on `#splash`. The node is removed when it ends. */
const FADE_MS = 280;

/** How many screens are still a wait rather than the app. */
let holds = 0;

/** Whether the app has asked for the splash at all — `hideSplash` in
 *  `index.tsx`. Nothing is taken down before it does. */
let asked = false;

/** One check in flight, so a burst of releases is still one decision. */
let checking = false;

const splashNode = (): HTMLElement | null => document.getElementById("splash");

/** Set by the inline script in `index.html` when it unhides the splash. */
const shownAt = (): number | null => {
  const at = (window as { __NAURIST_SPLASH_AT__?: unknown })
    .__NAURIST_SPLASH_AT__;
  return typeof at === "number" ? at : null;
};

const check = (): void => {
  checking = false;

  if (!asked || holds > 0) return;

  const splash = splashNode();
  if (!splash || splash.classList.contains("is-leaving")) return;

  // Still owed some of the minimum — come back when it is paid. The second
  // visit computes zero and falls through, so this cannot circle.
  const up = shownAt();
  const left = up === null ? 0 : Math.max(0, MINIMUM_MS - (Date.now() - up));
  if (left > 0) {
    schedule(left);
    return;
  }

  splash.classList.add("is-leaving");
  window.setTimeout(() => splash.remove(), FADE_MS);
};

const schedule = (delay = 0): void => {
  if (checking) return;
  checking = true;
  window.setTimeout(check, delay);
};

/**
 * Keeps the splash up while the caller is on screen, and answers with the
 * release that lets it go.
 *
 * For the screens that are a wait rather than the app. Anything with something
 * to say — a consent form, a failed workspace, an error — must *not* hold:
 * covering it with a brand mark is how a reader ends up staring at a logo
 * while the screen underneath asks them a question.
 */
export const holdSplash = (): (() => void) => {
  holds += 1;

  // Released once, however many times React hands the cleanup back.
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds -= 1;
    // Never in this task: a loader handing over to another releases before the
    // next one claims, and both happen inside one commit.
    schedule();
  };
};

/**
 * Keeps the splash up for as long as the calling component is mounted.
 *
 * Mount it with the waiting itself — `SessionLoading` is the claim, not the
 * guard that renders it — so the hold ends exactly when the wait does.
 */
export const useHeldSplash = (): void => {
  useEffect(() => holdSplash(), []);
};

/**
 * Says the app has the window, and takes the splash down once nothing is
 * holding it.
 *
 * `immediate` skips the wait, the holds and the fade, for the one caller that
 * wants the splash gone rather than finished with — a forms host, where it
 * should never have been up at all.
 *
 * Safe to call when there is no splash: on a forms host or a desktop the
 * inline script and the stylesheet have already seen to it, and under jsdom it
 * was never there. Safe to call twice.
 */
export const hideSplash = (options?: { immediate?: boolean }): void => {
  if (options?.immediate) {
    splashNode()?.remove();
    return;
  }

  asked = true;

  // Two frames, because one is not enough: the first fires before the paint
  // that React's render scheduled, and a splash lifted then uncovers the frame
  // *before* the app rather than the app. The second fires after it — by which
  // time the first screen has mounted and taken its hold, if it is one that
  // holds.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => schedule());
  });
};

/** Test seam. The module's counters outlive a single render, which is the
 *  point of them, so a suite that exercises two openings has to say where one
 *  ends. Not called by the app. */
export const resetSplashForTests = (): void => {
  holds = 0;
  asked = false;
  checking = false;
};
